import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getCurrentRates } from '../services/ExchangeRateService';
import AuthContext from './AuthContext';
import PlatformBridge from '../utils/PlatformBridge';
import { DEFAULT_CURRENCY, getCurrency, registerCurrencies } from '../utils/currency';

/**
 * Monedas habilitadas y cotizaciones del tenant. Mientras el tenant solo tenga UYU habilitada (el caso de
 * siempre), `multiCurrency` es false y ninguna pantalla muestra selectores de moneda.
 */
const CurrencyContext = createContext({
  defaultCurrency: DEFAULT_CURRENCY,
  enabledCurrencies: [DEFAULT_CURRENCY],
  currencies: [getCurrency(DEFAULT_CURRENCY)],
  catalog: [],
  multiCurrency: false,
  rates: {},
  rateViews: [],
  refresh: async () => null,
});

export const CurrencyProvider = ({ children }) => {
  const { accessToken } = useContext(AuthContext);
  const [state, setState] = useState({ enabledCurrencies: [DEFAULT_CURRENCY], rateViews: [], catalog: [] });

  const refresh = useCallback(async () => {
    try {
      const data = await getCurrentRates();
      registerCurrencies(data?.currencies);
      setState({
        catalog: data?.currencies || [],
        enabledCurrencies: data?.enabledCurrencies?.length ? data.enabledCurrencies : [DEFAULT_CURRENCY],
        rateViews: data?.rates || [],
      });
      return data;
    } catch (error) {
      // Sin cotizaciones el sistema sigue funcionando en UYU: no se bloquea nada por esto.
      console.error('Error fetching exchange rates:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const storedAccessToken = await PlatformBridge.getToken('accessToken');
      if (!active) return;
      if (!accessToken && !storedAccessToken) {
        setState({ enabledCurrencies: [DEFAULT_CURRENCY], rateViews: [], catalog: [] });
        return;
      }
      refresh();
    })();
    return () => { active = false; };
  }, [accessToken, refresh]);

  const value = useMemo(() => ({
    defaultCurrency: DEFAULT_CURRENCY,
    enabledCurrencies: state.enabledCurrencies,
    currencies: state.enabledCurrencies.map(getCurrency),
    // Monedas soportadas por el sistema (para activarlas/desactivarlas en Configuración > Monedas).
    catalog: state.catalog,
    multiCurrency: state.enabledCurrencies.length > 1,
    rates: Object.fromEntries(state.rateViews.map((view) => [view.currency, view.rate])),
    rateViews: state.rateViews,
    refresh,
  }), [state, refresh]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrencies = () => useContext(CurrencyContext);

export default CurrencyContext;
